/**
 * RepertoireCursor
 *
 * A cursor for the repertoire scene that renders as a rounded rectangle and
 * moves across a phrase from note to note. Unlike CursorMobile (designed for
 * mobile infinite-scroll lessons), this cursor:
 *
 * - Has no camera control (repertoire uses camera Y-scrolling managed by the scene)
 * - Supports linear phrase-to-phrase transitions (no circular 4-slot buffer)
 * - Fades out when it reaches the end of a phrase
 * - Supports count-in fade-in for the next phrase's cursor
 * - Calls onLastSlice/onFinishPlaying callbacks for phrase transitions
 *
 * The cursor reads the current playback position from a shared TimeKeeper via
 * the eventStream, then interpolates (lerps) between the last note position and
 * the next note position to produce smooth movement.
 */

import RoundedRect from 'Phaser/GameObjects/RoundedRect';
import { colors } from 'Phaser/config';
import { ICursorIterator } from 'Phaser/GameObjects/CursorMobile';
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { INoteGraphicsDatabase } from 'Phaser/Scenes/StaffExercise';
import Logger from 'Utils/Logger';

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function lerp(start: number, end: number, interpolant: number): number {
  return start * (1 - interpolant) + end * interpolant;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

// ---------------------------------------------------------------------------
// Callbacks
// ---------------------------------------------------------------------------

export type OnLastSliceCallback = (
  startTimestamp: number,
  remainingDuration: number,
  remainingDurationInSecs: number,
) => void;

export type OnFinishPlayingCallback = () => void;

// ---------------------------------------------------------------------------
// RepertoireCursor
// ---------------------------------------------------------------------------

export default class RepertoireCursor extends RoundedRect {
  /** Whether the cursor is enabled (visible and receiving updates) */
  private enabled: boolean = false;
  /** Whether the cursor is actively running (advancing through the phrase) */
  private running: boolean = false;

  /** The event stream providing the shared TimeKeeper */
  private eventStream: EventStreamPhaser2;
  /** The iterator tracking note positions and timestamps */
  private iterator: ICursorIterator;

  /** Timestamp offset — subtracts the phrase's start timestamp to get local time */
  private timestampOffset: number;
  /** The original cursor alpha (used for fade-in / fade-out calculations) */
  private readonly originalAlpha: number;

  /** Last note's X position (lerp start point) */
  private lastNoteXPosition: number;
  /** Last note's expected onset timestamp */
  private lastExpectedOnset: number = -1;
  /** The current interpolated X position */
  private interpolatedPosition: number = 0;

  /** Current tempo in BPM */
  private tempo: number = 0;

  /** Count-in duration — when set, the cursor is fading in */
  private countInDuration: number | undefined;

  /** Whether the cursor has been unpaused once (to prevent double finish callback) */
  private playedOnce: boolean = false;
  /** The highest local timestamp seen while this cursor was running. Used to
   *  detect timestamp jumps — if the cursor goes from near 0 to past
   *  totalDuration in one step, the transition is likely erroneous. */
  private peakLocalTimestamp: number = 0;
  /** Guard to absorb a brief timer reset after unpause */
  private waitForTimerReset: boolean = false;
  /** Debug: count update frames after unpause to log diagnostics */
  private debugFramesAfterUnpause: number = -1;
  /** Timer for the waitForTimerReset guard */
  private resetTimer: Phaser.Time.TimerEvent | undefined;
  /** Frame counter for sampled diagnostic logging */
  private updateFrameCount: number = 0;

  /** Active blink tween (metronome beat blink during count-in) */
  private blinkTween: Phaser.Tweens.Tween | undefined;

  // ---- Callbacks for phrase transitions ----
  private onLastSlice: OnLastSliceCallback | undefined;
  private onFinishPlaying: OnFinishPlayingCallback | undefined;
  private callbackScope: unknown;

  // ---- Public properties read by the scene ----
  /** Index of this cursor's phrase */
  readonly phraseIndex: number;
  /** Starting measure offset (global) for this phrase */
  startingMeasure: number = 0;
  /** Last played measure — tracked for pause/resume positioning */
  lastPlayedMeasure: number | undefined;
  /** When true, all visual output is suppressed (alpha forced to 0) while
   *  the cursor continues tracking position internally. Mirrors the SVG
   *  cursor's `visibility: hidden` CSS pattern. Set by the scene when the
   *  user toggles cursor visibility in settings. */
  private renderHidden: boolean = false;

  constructor(
    scene: INoteGraphicsDatabase & Phaser.Scene,
    initialX: number,
    initialY: number,
    cursorWidth: number,
    cursorHeight: number,
    eventStream: EventStreamPhaser2,
    iterator: ICursorIterator,
    onLastSlice: OnLastSliceCallback | undefined,
    onFinishPlaying: OnFinishPlayingCallback | undefined,
    callbackScope: unknown,
    hidden: boolean,
    timestampOffset: number,
    phraseIndex: number,
    cursorColor: number = colors.cursor,
  ) {
    super(
      scene,
      initialX,
      initialY,
      cursorWidth,
      cursorHeight,
      cursorWidth / 2,
      cursorColor,
      colors.cursorAlpha,
    );

    this.eventStream = eventStream;
    this.iterator = iterator;
    this.timestampOffset = timestampOffset;
    this.originalAlpha = colors.cursorAlpha;
    this.lastNoteXPosition = initialX;
    this.tempo = eventStream.timeKeeper.getBpm();
    this.onLastSlice = onLastSlice;
    this.onFinishPlaying = onFinishPlaying;
    this.callbackScope = callbackScope;
    this.phraseIndex = phraseIndex;

    this.setX(initialX - cursorWidth / 2);
    if (hidden) {
      this.setAlpha(0);
    }
  }

  // ---------------------------------------------------------------------------
  // Visibility
  // ---------------------------------------------------------------------------

  /** Suppress or restore visual rendering. When hidden, the cursor still
   *  tracks position internally (enabled/running unchanged) but draws at
   *  alpha 0. Mirrors the SVG cursor's `visibility: hidden` CSS pattern.
   *
   *  Called by the scene when the user toggles cursor visibility in settings.
   *  The renderHidden flag is checked in enable(), blink(), clearBlink(),
   *  and update() to ensure alpha is always forced to 0 when hidden. */
  setRenderHidden(hidden: boolean): void {
    Logger.info('[CURSOR-VIS] RepertoireCursor.setRenderHidden', {
      phraseIndex: this.phraseIndex,
      hidden,
      wasHidden: this.renderHidden,
    });
    this.renderHidden = hidden;
    if (hidden) {
      this.clearBlink(0);
    } else if (this.enabled) {
      this.setAlpha(this.originalAlpha);
    }
  }

  // ---------------------------------------------------------------------------
  // Enable / disable / pause / unpause
  // ---------------------------------------------------------------------------

  enable(): void {
    Logger.info('[CURSOR-VIS] RepertoireCursor.enable', {
      phraseIndex: this.phraseIndex,
      renderHidden: this.renderHidden,
      willSetAlpha: !this.renderHidden,
    });
    if (!this.renderHidden) {
      this.setAlpha(this.originalAlpha);
    }
    this.enabled = true;
  }

  disable(): void {
    this.running = false;
    this.enabled = false;
    this.setAlpha(0);
  }

  unpause(_inaccuracyCompensation?: number): void {
    this.waitForTimerReset = true;
    this.resetTimer?.remove();
    this.resetTimer = this.scene.time.addEvent({
      callback: () => {
        this.waitForTimerReset = false;
      },
      delay: 50,
      callbackScope: this,
    });
    this.clearBlink();
    // When transitioning from count-in to running mode, snap the cursor's
    // position reference to the current interpolated position and reset
    // lastExpectedOnset to 0. During count-in, lastExpectedOnset is set
    // to a negative value (by startCountingIn) that works with the
    // count-in's eased interpolation. When switching to the running
    // mode's linear interpolation, that negative reference causes the
    // cursor to briefly appear behind the first note if the local
    // timestamp is even slightly negative (due to stale cachedCurrentTime
    // in the NativeTimeKeeper). Resetting to 0 ensures the running-mode
    // lerp starts from a clean reference point at the position the
    // count-in animation already reached.
    //
    // On the resume path (no count-in), lastExpectedOnset was set by
    // setStart() to the correct resumed position and must not be reset.
    const wasCountingIn = this.countInDuration !== undefined;
    this.calculatePosition();
    if (wasCountingIn) {
      this.lastNoteXPosition = this.interpolatedPosition;
      this.lastExpectedOnset = 0;
    }
    this.running = true;
    this.peakLocalTimestamp = 0;
    this.countInDuration = undefined;
    this.debugFramesAfterUnpause = 0; // Start diagnostic logging
  }

  pause(): void {
    this.running = false;
    this.resetTimer?.remove();
    this.resetTimer = undefined;
    this.clearBlink();
  }

  /**
   * Reposition the cursor to a specific note position and roll back the
   * iterator to a given measure. Used on pause to jump back to the first
   * note of the last completed measure.
   */
  setStart(xPosition: number, timestamp: number, measureOffset: number): void {
    // Rewind the iterator to the start of measureOffset and advance to
    // timestamp so its currentTimeStamp matches lastExpectedOnset. Without
    // this, mid-measure bracket positions leave the iterator behind, causing
    // the cursor's update() loop to ratchet forward and flash visually.
    this.iterator.rollbackToMeasure(measureOffset, timestamp);
    this.lastNoteXPosition = xPosition;
    this.lastExpectedOnset = timestamp;
    this.moveTo(xPosition);
  }

  // ---------------------------------------------------------------------------
  // Count-in (fade-in for next phrase's cursor)
  // ---------------------------------------------------------------------------

  /**
   * Begin the count-in animation. The cursor fades in from alpha 0 over the
   * given duration while following the lerp position.
   */
  startCountingIn(_startTimestamp: number, lastOnset: number, duration: number): void {
    this.enabled = true;
    this.lastNoteXPosition = this.x;
    this.countInDuration = duration;
    this.lastExpectedOnset = -lastOnset;
  }

  // ---------------------------------------------------------------------------
  // Blink (metronome beat)
  // ---------------------------------------------------------------------------

  blink(durationMs: number): void {
    if (!this.enabled || this.renderHidden) return;
    this.clearBlink();
    // Yoyo: alpha animates originalAlpha → 0 → originalAlpha across the
    // beat, so each blink cycle ENDS where it started (visible). The
    // previous implementation was a one-shot fade to 0 that relied on the
    // next tickUpdate's blink() call to reset alpha — during tempo-slider
    // drag, drag events fire blink() but gaps between events allowed the
    // tween to run to completion, leaving the cursor invisible (alpha=0).
    // With yoyo, gaps end cleanly at originalAlpha; the cursor never
    // lingers at 0 between blinks.
    this.blinkTween = this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: durationMs / 2,
      yoyo: true,
      onUpdate: () => this.draw(),
      callbackScope: this,
    });
  }

  clearBlink(alpha: number = this.originalAlpha): void {
    this.blinkTween?.stop();
    this.setAlpha(this.renderHidden ? 0 : alpha);
  }

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------

  /**
   * Reset the cursor with a new iterator. Used during soft-refresh and
   * initial setup.
   */
  reset(iterator: ICursorIterator, timestampOffset: number, measureOffset: number): void {
    this.startingMeasure = measureOffset;
    this.iterator = iterator;
    this.timestampOffset = timestampOffset;
    this.running = false;
    this.playedOnce = false;
    this.peakLocalTimestamp = 0;
    this.countInDuration = undefined;
    this.waitForTimerReset = false;
    this.lastExpectedOnset = -1;
  }

  // ---------------------------------------------------------------------------
  // Position accessors
  // ---------------------------------------------------------------------------

  setLastNotePos(xPosition: number): void {
    this.lastNoteXPosition = xPosition;
  }

  getLastPlayedMeasure(): number {
    const lastMeasure = this.startingMeasure + this.iterator.lastPlayedMeasure;
    if (this.lastPlayedMeasure === undefined || lastMeasure > this.lastPlayedMeasure) {
      this.lastPlayedMeasure = lastMeasure;
    }
    return this.lastPlayedMeasure;
  }

  getTimestamp(): number {
    return this.eventStream.timeKeeper.audioTimeToMeasureTimestamp();
  }

  // ---------------------------------------------------------------------------
  // Core update loop
  // ---------------------------------------------------------------------------

  update(): void {
    if (!this.enabled) return;

    const currentTimestamp = this.getCurrentLocalTimestamp();

    // ---- Diagnostic: log first 5 frames after unpause to trace what blocks movement ----
    if (this.debugFramesAfterUnpause >= 0 && this.debugFramesAfterUnpause < 5) {
      const tkPaused = this.eventStream.timeKeeper.isPaused();
      const tkTimestamp = this.eventStream.timeKeeper.audioTimeToMeasureTimestamp();
      const iterTotalDur = this.iterator.totalDuration();
      const iterTsOffset = this.iterator.timestampOffset;
      const iterCurrentTs = (this.iterator as { currentTimeStamp?: number }).currentTimeStamp;
      console.log(
        `[RESUME-DBG] cursor.update frame=${this.debugFramesAfterUnpause} running=${this.running} currentLocalTs=${currentTimestamp.toFixed(4)} TKPaused=${tkPaused} TKTs=${tkTimestamp?.toFixed(4)} iterCurrentTs=${iterCurrentTs} iterTsOffset=${iterTsOffset} iterTotalDur=${iterTotalDur} lastExpectedOnset=${this.lastExpectedOnset} lastNoteX=${this.lastNoteXPosition?.toFixed(1)} cursorX=${this.x?.toFixed(1)}`,
      );
      this.debugFramesAfterUnpause++;
    }

    // ---- Count-in fade-in ----
    if (this.countInDuration !== undefined) {
      this.interpolatedPosition = this.calculatePosition();
      this.moveTo(this.interpolatedPosition);

      const progressRatio = clamp(
        (currentTimestamp - this.lastExpectedOnset) / this.countInDuration,
        0,
        1,
      );
      const easedProgress = Math.pow(progressRatio, 4);
      const fadeInAlpha = lerp(0, this.originalAlpha, easedProgress);
      if (easedProgress === 1) {
        this.countInDuration = undefined;
      }
      this.setAlpha(this.renderHidden ? 0 : fadeInAlpha);
    }

    // ---- Track peak local timestamp for jump detection ----
    if (this.running && currentTimestamp > this.peakLocalTimestamp) {
      this.peakLocalTimestamp = currentTimestamp;
    }

    // ---- Finish detection ----
    // Guard: require the cursor to have progressively advanced through at
    // least half the phrase (peakLocalTimestamp > totalDuration * 0.5).
    // This prevents a premature transition when audioTimeToMeasureTimestamp()
    // suddenly jumps past the end — e.g. due to a stale cachedCurrentTime in
    // the NativeTimeKeeper, a timestamp glitch after unpause, or a startTime
    // mismatch between the JS and native schedulers.
    const totalDuration = this.iterator.totalDuration();
    const hasAdvancedProgressively = this.peakLocalTimestamp > totalDuration * 0.5;
    if (
      this.running &&
      currentTimestamp >= totalDuration &&
      !this.waitForTimerReset &&
      hasAdvancedProgressively
    ) {
      Logger.debug('[OSMDRepertoire] Cursor finish detected — calling onFinishPlaying', {
        phraseIndex: this.phraseIndex,
        currentTimestamp,
        totalDuration,
        peakLocalTimestamp: this.peakLocalTimestamp,
        timestampOffset: this.timestampOffset,
        running: this.running,
        playedOnce: this.playedOnce,
        waitForTimerReset: this.waitForTimerReset,
        timeKeeperTimestamp: this.eventStream.timeKeeper.audioTimeToMeasureTimestamp(),
        timeKeeperPaused: this.eventStream.timeKeeper.isPaused(),
      });
      this.onFinishPlaying?.call(this.callbackScope);
      this.playedOnce = true;
      this.running = false;
    } else if (
      this.running &&
      currentTimestamp >= totalDuration &&
      !this.waitForTimerReset &&
      !hasAdvancedProgressively
    ) {
      // Log when finish would have fired but was blocked by the safety guard.
      // This helps diagnose timestamp jumps that would cause premature transitions.
      Logger.warn(
        '[OSMDRepertoire] Cursor finish BLOCKED — timestamp jumped past end without progressive advancement',
        {
          phraseIndex: this.phraseIndex,
          currentTimestamp,
          totalDuration,
          peakLocalTimestamp: this.peakLocalTimestamp,
          timestampOffset: this.timestampOffset,
          timeKeeperTimestamp: this.eventStream.timeKeeper.audioTimeToMeasureTimestamp(),
          timeKeeperPaused: this.eventStream.timeKeeper.isPaused(),
        },
      );
    }

    // ---- Early return if not actively running ----
    const earlyReturnTkPaused = this.eventStream.timeKeeper.isPaused();
    const earlyReturnTkTs = this.eventStream.timeKeeper.audioTimeToMeasureTimestamp();
    const earlyReturnEndTs = this.iterator.totalDuration() + this.iterator.timestampOffset;
    const earlyReturnPastEnd = earlyReturnTkTs >= earlyReturnEndTs;

    // Sampled diagnostic: log every ~60 frames to track cursor state
    this.updateFrameCount++;
    if (this.updateFrameCount % 60 === 0) {
      const iterState = this.iterator as unknown as {
        currentTimeStamp?: number;
        indx?: number;
        endReached?: boolean;
        totalLength?: number;
      };
      console.log(
        `[CURSOR-UPDATE-DBG] phraseIdx=${this.phraseIndex} enabled=${this.enabled} running=${this.running} ` +
          `localTs=${currentTimestamp.toFixed(3)} TKTs=${earlyReturnTkTs.toFixed(3)} ` +
          `TKPaused=${earlyReturnTkPaused} pastEnd=${earlyReturnPastEnd} ` +
          `endTs=${earlyReturnEndTs.toFixed(3)} cursorX=${this.x?.toFixed(1)} ` +
          `iterIndx=${iterState.indx} iterEndReached=${iterState.endReached} ` +
          `iterCurrentTs=${iterState.currentTimeStamp} alpha=${this.alpha.toFixed(2)}`,
      );
    }

    if (!this.running || earlyReturnTkPaused || earlyReturnPastEnd) {
      return;
    }

    // ---- Update tempo ----
    const currentBpm = this.eventStream.timeKeeper.getBpm();
    if (this.tempo !== currentBpm) {
      this.tempo = currentBpm;
    }

    // ---- Calculate and apply position ----
    this.interpolatedPosition = this.calculatePosition();
    this.moveTo(this.interpolatedPosition);

    // ---- Fade-out at end of phrase ----
    if (this.iterator.EndReached) {
      const lastNoteDuration = this.iterator.lastNoteDuration();
      const remainingFraction =
        (this.iterator.totalDuration() - currentTimestamp) / lastNoteDuration;
      const fadeOutAlpha = lerp(this.originalAlpha, 0, Math.pow(1 - remainingFraction, 4));
      this.setAlpha(this.renderHidden ? 0 : fadeOutAlpha);
    }

    // ---- Advance to next slice ----
    const comparisonTimestamp = this.iterator.EndReached
      ? this.iterator.totalDuration()
      : this.iterator.currentTimeStamp;
    const shouldAdvance = this.running && currentTimestamp >= comparisonTimestamp;
    if (shouldAdvance) {
      this.lastExpectedOnset = this.iterator.currentTimeStamp;
      this.lastNoteXPosition = this.interpolatedPosition;
      this.iterator.next(
        this.eventStream.timeKeeper.getBpm(),
        this.onLastSlice,
        this.callbackScope,
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  private getCurrentLocalTimestamp(): number {
    return this.eventStream.timeKeeper.audioTimeToMeasureTimestamp() - (this.timestampOffset ?? 0);
  }

  /**
   * Calculate the interpolated X position between the last note and the
   * next note based on the current timestamp progress.
   */
  private calculatePosition(): number {
    const nextXPosition = this.iterator.left(true);
    const nextTimestamp = this.iterator.getNextTimestamp();
    const timeDifference = nextTimestamp - this.lastExpectedOnset;
    const currentTimestamp = this.getCurrentLocalTimestamp();
    let progress = currentTimestamp - this.lastExpectedOnset;

    if (this.countInDuration) {
      progress = currentTimestamp + this.countInDuration;
    }

    let interpolant = timeDifference ? progress / timeDifference : 1;
    if (this.countInDuration) {
      interpolant = clamp(Math.pow(interpolant, 4), 0, 1);
    } else if (this.running) {
      // In running mode (not count-in), clamp the interpolant so the
      // cursor never goes backwards past lastNoteXPosition. A slightly
      // negative local timestamp (from stale cachedCurrentTime in the
      // NativeTimeKeeper) can produce a negative progress, which without
      // clamping would place the cursor before the current note — i.e.,
      // at the start of the staff system instead of near the first note.
      interpolant = Math.max(0, interpolant);
    }

    return lerp(this.lastNoteXPosition, nextXPosition, interpolant);
  }

  private moveTo(xPosition: number): void {
    this.setX(xPosition - this.width / 2);
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  destroy(): void {
    this.blinkTween?.stop();
    this.resetTimer?.remove();
    super.destroy();
  }
}
